import { PropsWithChildren } from 'react';
import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { SurfaceHeader } from '@frontend/surface-header';
import { theme } from '@frontend/theme';
import { ButtonBar, TextLink, Text, useTooltip, Button, BannerNotification } from '@frontend/design-system';
import {
  ScheduleRequestDetailsContextProvider,
  useScheduleRequestDetailsContext,
} from '../Context/ScheduleRequestDetailsContext';
import { ScheduleRequestModalFormContext } from '../Context/ScheduleRequestModalFormContext';
import { useScheduleRequestDetails } from '../hooks/use-schedule-request-details';
import { ScheduleRequestsTrackingIds } from '../trackingIds';
import { ScheduleRequestModalFormContainerV3 } from './ScheduleRequestModalFormContainerV3';
import { ScheduleRequestModalFormV2 } from './ScheduleRequestModalFormV2';
import { ScheduleRequestModalManager } from './ScheduleRequestModalManager';
import { ScheduleRequestModalTypeEnum } from './types';

const ScheduleRequestDetailsProvider = ({
  children,
  providerValue,
  ...props
}: PropsWithChildren<ReturnType<typeof useScheduleRequestDetails>>) => {
  return (
    <ScheduleRequestModalFormContext.Provider value={providerValue}>
      <ScheduleRequestDetailsContextProvider value={props}>{children}</ScheduleRequestDetailsContextProvider>
    </ScheduleRequestModalFormContext.Provider>
  );
};

const ScheduleRequestDetailsHeader = ({ onClose }: { onClose: () => void }) => {
  const { t } = useTranslation('scheduleCalendarRequest');

  return (
    <SurfaceHeader.Root noBorder>
      <SurfaceHeader.Row>
        <SurfaceHeader.Title>{t('Review Appointment Request')}</SurfaceHeader.Title>
        <SurfaceHeader.Actions>
          <SurfaceHeader.Icon iconName='x' label={t('Close Appointment Request Modal')} onClick={onClose} />
        </SurfaceHeader.Actions>
      </SurfaceHeader.Row>
      <SurfaceHeader.Body>{t('Review and confirm the appointment request details below.')}</SurfaceHeader.Body>
    </SurfaceHeader.Root>
  );
};

const ScheduleRequestDetailsBody = () => {
  const { isV3Enabled, appointmentWritebackSourceTenants, patientWritebackSourceTenants, isLoadingSourceTenants } =
    useScheduleRequestDetailsContext();

  return (
    <section css={{ flex: 1, backgroundColor: theme.colors.neutral5, padding: theme.spacing(3), overflow: 'auto' }}>
      {isV3Enabled ? (
        <ScheduleRequestModalFormContainerV3
          appointmentWritebackSourceTenants={appointmentWritebackSourceTenants}
          patientWritebackSourceTenants={patientWritebackSourceTenants}
          isLoadingSourceTenants={isLoadingSourceTenants}
        />
      ) : (
        <ScheduleRequestModalFormV2
          appointmentWritebackSourceTenants={appointmentWritebackSourceTenants}
          isLoadingSourceTenants={isLoadingSourceTenants}
        />
      )}
    </section>
  );
};

const ScheduleRequestDetailsFooter = () => {
  const tooltip = useTooltip();
  const { t } = useTranslation('scheduleCalendarRequest');
  const {
    personId,
    locationId,
    phoneNumber,
    canSendMessages,
    handleCloseScheduleRequestModal,
    closeActionsModal,
    disableApproveBtn,
    handlePostApprove,
    modalType,
    validateAndApproveRequest,
    fullName,
    handleCloseModal,
    setModalType,
    messageOnClick,
    backendError,
  } = useScheduleRequestDetailsContext();

  return (
    <>
      <footer css={scheduleRequestModalFooterStyle}>
        {!canSendMessages && (
          <tooltip.Tooltip {...tooltip.tooltipProps}>
            {t('You do not have permission to send text messages')}
          </tooltip.Tooltip>
        )}
        <TextLink
          weight='bold'
          css={{ display: 'flex', alignItems: 'center', width: 120 }}
          trackingId={ScheduleRequestsTrackingIds.requestFormMessageLinkBtn}
          onClick={async () => {
            await messageOnClick({ personId, threadGroupId: locationId, personPhone: phoneNumber });
            handleCloseScheduleRequestModal();
            closeActionsModal();
          }}
          disabled={!canSendMessages}
          {...(!canSendMessages ? tooltip.triggerProps : {})}
        >
          <Icon css={{ marginRight: theme.spacing(0.5) }} color='primary' name='message' />
          {t('Message')}
        </TextLink>
        <ButtonBar css={{ padding: 0 }}>
          <Button
            variant='secondary'
            trackingId={ScheduleRequestsTrackingIds.requestCancelBtn}
            onClick={handleCloseScheduleRequestModal}
          >
            <Text as='span' weight='bold'>
              {t('Cancel')}
            </Text>
          </Button>
          <Button
            variant='primary'
            disabled={disableApproveBtn}
            css={{ display: 'flex', alignItems: 'center', gap: theme.spacing(0.5) }}
            trackingId={ScheduleRequestsTrackingIds.requestApproveBtn}
            onClick={handlePostApprove}
          >
            <Icon name='check-small' color='white' />
            <Text as='span' weight='bold' color='white'>
              {t('Approve')}
            </Text>
          </Button>
        </ButtonBar>
      </footer>
      <ScheduleRequestModalManager
        modalType={modalType}
        onConfirm={() => {
          const writebackWasSkipped =
            modalType === ScheduleRequestModalTypeEnum.WRITEBACK_ERROR ||
            modalType === ScheduleRequestModalTypeEnum.NO_WRITEBACKS_ERROR;
          validateAndApproveRequest('', writebackWasSkipped);
        }}
        fullName={fullName}
        onClose={handleCloseModal}
        setModalType={setModalType}
        backendError={backendError}
      />
    </>
  );
};

const WritebackWarningBanner = () => {
  const { t } = useTranslation('scheduleCalendarRequest');

  return (
    <BannerNotification status='warn' fullWidth css={{ margin: theme.spacing(2) }} hidden>
      {t(
        ' This location does not support appointment writebacks, but you are viewing this request modal as debug mode is enabled. This may cause unexpected behaviour.'
      )}
    </BannerNotification>
  );
};

export const ScheduleRequestDetails = Object.assign(ScheduleRequestDetailsProvider, {
  Header: ScheduleRequestDetailsHeader,
  Body: ScheduleRequestDetailsBody,
  Footer: ScheduleRequestDetailsFooter,
  WritebackWarningBanner,
});

const scheduleRequestModalFooterStyle = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(4, 3),
  backgroundColor: theme.colors.white,
  boxShadow: theme.shadows.light,
});
